import { forwardRef, KeyboardEvent, useRef } from "react";
import { InputSize } from "../standard-input-size";
import {
  TextInputProps,
  TextInput,
} from "@/block-system/brickz/components/ui/TextInput";
import { XIcon, Search } from "lucide-react";
import { cn } from "@/utils/cn";
import { mergeRefs } from "@/lib/utils/mergeRefs";

export const SearchInput = forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref) => {
    const renderClearButton =
      typeof props.value === "string" &&
      props.value.length > 0 &&
      props.onChange != null;

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape" && props.onChange) props.onChange("");
    };

    const inputRef = useRef<HTMLInputElement>(null);
    const mergedRef = mergeRefs(ref, inputRef);

    const handleClear = () => {
      props.onChange?.("");
      inputRef.current?.focus();
    };

    return (
      <TextInput
        ref={mergedRef}
        renderIconBefore={() => <Search size={getIconSize(props.size)} />}
        renderIconAfter={
          renderClearButton
            ? () => <ClearButton onClear={handleClear} />
            : undefined
        }
        placeholder={props.placeholder ?? "Search..."}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  }
);

function ClearButton({ onClear }: { onClear: () => void }) {
  return (
    <button
      className="-mx-2 flex cursor-pointer items-center px-2"
      type="button"
      onClick={onClear}
    >
      <span className="sr-only">Clear search</span>
      <XIcon
        aria-hidden={true}
        className={cn("h-4 w-4 rounded-full bg-muted-foreground p-0.5")}
        color={"hsl(var(--card))"}
      />
    </button>
  );
}

SearchInput.displayName = "SearchInput";

function getIconSize(size: InputSize): number {
  switch (size) {
    case undefined:
    case "medium":
      return 16;

    case "small":
      return 12;

    default:
      const _exhaustiveCheck: never = size;
      return _exhaustiveCheck;
  }
}
